/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). The parts plan's builds come
 * from the demand forecast of each panel and kit, so there is one set of numbers.
 * Month M1 is the first forecast month, M2 the second, and so on. A change on
 * the forecast (a buyer's override, or the assistant) changes the builds, and a
 * change to a build on the parts plan is a fixed month on the forecast.
 */
import { useMemo } from 'react';
import { BUCKET_COUNT, FG_SKUS, FINISHED_GOODS } from './bom-data';
import type { FgSku } from './bom-data';
import { fgRecord } from './bom-logic';
import type { FgForecast } from './bom-logic';
import { AXIS, getSpec } from './forecast-data';
import { buildView } from './forecast-engine';
import {
  clearForecastChanges,
  getForecastState,
  recordChange,
  setPinnedMonth,
  useForecastState,
} from './forecast-store';
import type { ApprovalStatus, ForecastDemoState } from './forecast-types';

export interface BuildsPlan {
  /** Builds the parts plan runs on: the forecast with every buyer change. */
  builds: FgForecast;
  /** Builds from the model forecast, before any buyer change. */
  model: FgForecast;
  /** Months (across all three) where builds differ from the model forecast. */
  changedCells: number;
  /** Builds added (or removed, when negative) across all months. */
  changedBuilds: number;
  /** Panels and kits whose forecast a buyer changed. */
  changedSkus: FgSku[];
  approval: Record<FgSku, ApprovalStatus>;
}

function seriesFor(sku: FgSku): string {
  const found = FINISHED_GOODS.find((fg) => fg.sku === sku);
  if (!found) throw new Error(`Unknown finished good ${sku}`);
  return found.seriesId;
}

function viewFor(sku: FgSku, state: ForecastDemoState) {
  const spec = getSpec(seriesFor(sku));
  if (!spec) throw new Error(`No forecast for ${sku}`);
  return buildView(spec, state);
}

/** The builds plan for a forecast state. Pure, so tests can call it directly. */
export function buildsPlanFor(state: ForecastDemoState): BuildsPlan {
  const views = fgRecord((sku) => viewFor(sku, state));
  const builds = fgRecord((sku) => views[sku].adjusted.slice(0, BUCKET_COUNT));
  const model = fgRecord((sku) =>
    views[sku].result.forecast.slice(0, BUCKET_COUNT),
  );
  let changedCells = 0;
  let changedBuilds = 0;
  const changedSkus: FgSku[] = [];
  for (const sku of FG_SKUS) {
    let changed = false;
    builds[sku].forEach((units, bucket) => {
      const base = model[sku][bucket] ?? 0;
      if (units === base) return;
      changed = true;
      changedCells += 1;
      changedBuilds += units - base;
    });
    if (changed) changedSkus.push(sku);
  }
  return {
    builds,
    model,
    changedCells,
    changedBuilds,
    changedSkus,
    approval: fgRecord((sku) => views[sku].approval.status),
  };
}

export function useBuildsPlan(): BuildsPlan {
  const state = useForecastState();
  return useMemo(() => buildsPlanFor(state), [state]);
}

/**
 * Sets one month of builds. A value the forecast would give anyway (the model,
 * or the model with the buyer's growth change) clears the fixed month instead
 * of storing a copy of it.
 */
export function editBuilds(sku: FgSku, bucket: number, units: number): void {
  const month = AXIS.forecast[bucket];
  const id = seriesFor(sku);
  const spec = getSpec(id);
  if (!month || !spec) return;
  const state = getForecastState();
  const others = Object.fromEntries(
    Object.entries(state.pins[id] ?? {}).filter(([key]) => key !== month.key),
  );
  const unfixed = buildView(spec, {
    ...state,
    pins: { ...state.pins, [id]: others },
  }).adjusted[bucket];
  setPinnedMonth(id, month.key, units === unfixed ? null : units, false);
}

/**
 * Writes the change log entry for a build the buyer finished editing. The edit
 * itself applies on every keystroke, so the log waits for the field to settle.
 */
export function logBuildsEdit(sku: FgSku, bucket: number): void {
  const month = AXIS.forecast[bucket];
  const id = seriesFor(sku);
  if (!month) return;
  const state = getForecastState();
  const units = state.pins[id]?.[month.key];
  if (units === undefined) return;
  const text = `Set ${month.label} to ${units} builds on the parts plan`;
  const latest = state.audit.find((entry) => entry.seriesId === id);
  if (latest?.text === text) return;
  recordChange(id, text);
}

/** Takes every buyer change off the panel and kit forecast. */
export function resetBuilds(): void {
  clearForecastChanges(
    FINISHED_GOODS.map((fg) => fg.seriesId),
    'Reset the builds to the model forecast from the parts plan',
  );
}
